// 우편 적용(docs/PARTY-TRADE-PRD.md 1.1·3.6): 서버 우편함에서 받은(claim) 골드·장비를 장비 장부(gear.v1)에 넣고 저장까지 끝낸다.
// 연산 id = mk:m<우편 id>. 같은 우편을 다시 받아도(ack 전 재전송·두 탭) 장부가 같은 id를 한 번만 적용한다.
// 호출 순서(UI 레인 src/market/mailClient.ts): claim → applyMail → (true일 때만) ack.
// C1a: 강화석(stones)·보호 주문서(protect) 우편 — 넷 레이드 보상을 솔로와 같은 표로. 보호 주문서는 받는 순간 아직 못 쓰면(지역6 전) 강화석 alt개로 바꿔 넣는다
// (솔로 raidReward의 PROTECT_AS_STONES와 같은 규칙). 같은 우편은 연산 id가 같아 어느 쪽으로 들어갔든 다시 들어가지 않는다.
import type { GearItem } from '../shared/gear';
import type { GearCode } from '../shared/gearLedger';
import type { StoneTier } from '../shared/content/economy';
import type { GearStore } from './gear';
import type { ProfileStore } from './profile';

/** 서버 우편 한 통(GET /api/mail·POST /api/mail/claim 응답의 MailView 중 필요한 것) */
export interface MailLike {
  id: number;
  kind: 'gold' | 'item' | 'stones' | 'protect';
  gold: number;
  item: GearItem | null;
  /** stones·protect: 수량·강화석 등급·(protect) 대신 줄 강화석 수 */
  grant?: { n: number; tier: number; alt: number } | null;
}
/** applyMail이 읽는 게임 쪽(GameSim이 그대로 맞는다) */
export interface MailGame {
  readonly gear: Pick<GearStore, 'applyMarket' | 'protectUnlocked'>;
  readonly profile: Pick<ProfileStore, 'mode'>;
}

/** 마지막 적용 결과 코드(UI 문구용: bag_full이면 "가방을 비운 뒤 받기") */
export let lastMailCode: GearCode | 'bad_mail' = 'ok';

/** 우편 id → 장부 연산 id(서버 거래 id에서 결정적) */
export const mailOpId = (id: number): string => 'mk:m' + id;

/**
 * 우편 한 통을 장부에 넣고 저장 확정까지 기다린다. true = 이제 ack해도 된다(새로 넣었거나 이미 들어 있음).
 * false = ack하지 말 것(가방 가득·저장 실패·다른 탭이 거래 권한·잘못된 우편) — 서버가 다음 접속에 다시 준다.
 */
export async function applyMail(game: MailGame, mail: MailLike): Promise<boolean> {
  if (!mail || !Number.isSafeInteger(mail.id) || mail.id <= 0) { lastMailCode = 'bad_mail'; return false; }
  const id = mailOpId(mail.id);
  let code: GearCode;
  if (mail.kind === 'gold') {
    if (!Number.isSafeInteger(mail.gold) || mail.gold <= 0) { lastMailCode = 'bad_mail'; return false; }
    // 골드가 움직이는 연산은 이 탭이 상점 장부 writer일 때만(다른 탭과 잔액이 엇갈리지 않게 — gearCommand와 같은 규칙)
    code = game.profile.mode !== 'ok' ? 'readonly' : game.gear.applyMarket({ id, t: 'mkGold', d: mail.gold });
  } else if (mail.kind === 'item' && mail.item) {
    code = game.gear.applyMarket({ id, t: 'mkIn', item: mail.item });
  } else if ((mail.kind === 'stones' || mail.kind === 'protect') && mail.grant) {
    const { n, tier, alt } = mail.grant;
    const tierOk = Number.isSafeInteger(tier) && tier >= 0 && tier <= 3;
    if (mail.kind === 'stones') {
      if (!tierOk || !Number.isSafeInteger(n) || n < 1 || n > 999) { lastMailCode = 'bad_mail'; return false; }
      code = game.gear.applyMarket({ id, t: 'stones', tier: tier as StoneTier, n });
    } else if (game.gear.protectUnlocked) {
      if (!Number.isSafeInteger(n) || n < 1 || n > 9) { lastMailCode = 'bad_mail'; return false; }
      code = game.gear.applyMarket({ id, t: 'protect', n });
    } else {
      // 지역6 전: 보호 주문서를 쓸 수 없다 → 같은 연산 id로 강화석 alt개(alt 0이면 받을 것이 없으니 바로 끝 — ack해도 된다)
      if (!tierOk || !Number.isSafeInteger(alt) || alt < 0 || alt > 99) { lastMailCode = 'bad_mail'; return false; }
      if (alt === 0) { lastMailCode = 'ok'; return true; }
      code = game.gear.applyMarket({ id, t: 'stones', tier: tier as StoneTier, n: alt });
    }
  } else { lastMailCode = 'bad_mail'; return false; }
  lastMailCode = code;
  // 저장은 applyMarket 안에서 동기(localStorage)로 끝난다. 호출자가 await로 다음 단계(ack)를 이을 수 있게 비동기 함수로 둔다
  return code === 'ok' || code === 'duplicate';
}
